import React, {useCallback, useEffect, useMemo, useState, useRef} from 'react';
import {
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  FolderOpenIcon,
  PencilIcon,
  PlusIcon,
  ShareIcon,
  TrashIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import {useCollectionsStore} from '@/stores/collectionsStore';
import {useAuthStore} from '@/stores/authStore';
import {type Collection, type CollectionQuery, CollectionsService, CollectionSharingService, type Folder} from '@/services/api';
import {toast} from 'react-hot-toast';
import {ContextMenu, type ContextMenuItem} from './ContextMenu';
import {EditNameModal} from './EditNameModal';
import {EditQueryModal} from './EditQueryModal';
import {ConfirmationModal} from './ConfirmationModal';
import {CollectionSharingModal} from '../collections/CollectionSharingModal';
import {useDataSourceStore} from '@/stores/dataSourceStore';
import {useConnectionStore} from '@/stores/connectionStore';
import {DraggableQuery} from './DraggableQuery';
import {DropZone} from './DropZone';
import {type DragItem, DragItemTypes, type DropTarget} from './dnd-types';
import {COLLECTION_EVENTS, dispatchCollectionEvent, subscribeToCollectionEvent} from '@/utils/collectionEvents';
import {collectAllQueryIds} from '@/utils/queryTreeHelpers';
import {useQueryLibrarySelectionStore} from '@/stores/queryLibrarySelectionStore';
import {useCollectionTreeStateStore} from '@/stores/collectionTreeStateStore';
import {collectAllSubcollectionIds, collectAllFolderIds, collectAllSubfolderIds} from '@/utils/collectionTreeHelpers';

// Shared hook for confirmation modal state
function useConfirmationModal() {
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean
    title: string
    message: string
    onConfirm: () => void
    isDangerous?: boolean
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    isDangerous: false
  });

  const showConfirmation = useCallback((title: string, message: string, onConfirm: () => void, isDangerous?: boolean) => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      onConfirm,
      isDangerous
    });
  }, []);
  
  const closeConfirmation = useCallback(() => {
    setConfirmationModal(prev => ({ ...prev, isOpen: false }));
  }, []);

  return { confirmationModal, showConfirmation, closeConfirmation };
}

interface CollectionTreeProps {
  onQuerySelect?: (query: CollectionQuery & { collectionId: string }) => void;
  onCreateCollection?: () => void;
  onCreateQuery?: (collectionId: string, folderId?: string) => void;
  onCreateFolder?: (collectionId: string, parentFolderId?: string) => void;
  selectedQueryId?: string;
  className?: string;
}

type ContextMenuState = {
  position: { x: number; y: number };
  type: 'collection' | 'folder' | 'query';
  item: Collection | Folder | CollectionQuery;
  collectionId?: string;
} | null;

type EditModalState = {
  type: 'collection' | 'folder' | 'query';
  item: Collection | Folder | CollectionQuery;
  collectionId?: string;
} | null;

interface TreeNodeProps {
  collection: Collection;
  queries: CollectionQuery[];
  level: number;
  onQuerySelect?: (query: CollectionQuery & { collectionId: string }) => void;
  onCreateQuery?: (collectionId: string, folderId?: string) => void;
  onCreateFolder?: (collectionId: string, parentFolderId?: string) => void;
  selectedQueryId?: string;
  onRefreshNeeded?: () => void;
  parentAllQueryIds?: string[];
}

interface FolderNodeProps {
  folder: Folder;
  collectionId: string;
  queries: CollectionQuery[];
  subfolders: Folder[];
  level: number;
  onQuerySelect?: (query: CollectionQuery & { collectionId: string }) => void;
  onCreateQuery?: (collectionId: string, folderId?: string) => void;
  selectedQueryId?: string;
  onRefreshNeeded?: () => void;
  allQueryIds: string[];
  collectionPermissionLevel?: string;
}

const TreeNode: React.FC<TreeNodeProps> = ({ 
  collection, 
  level, 
  onQuerySelect, 
  onCreateQuery,
  onCreateFolder,
  selectedQueryId,
  onRefreshNeeded,
  parentAllQueryIds
}) => {
  const { isCollectionExpanded, toggleCollectionWithChildren } = useCollectionTreeStateStore();
  const isExpanded = collection.id ? isCollectionExpanded(collection.id) : false;
  const [contextMenu, setContextMenu] = useState<ContextMenuState>(null);
  const [editModal, setEditModal] = useState<EditModalState>(null);
  const { confirmationModal, showConfirmation, closeConfirmation } = useConfirmationModal();
  const [collectionDetails, setCollectionDetails] = useState<{
    queries?: CollectionQuery[];
    folders?: Folder[];
    subcollections?: Collection[];
  } | null>(null);
  const [, setIsLoadingDetails] = useState(false);
  const [detailsVersion, setDetailsVersion] = useState(0); // Force re-fetch when incremented

  const { updateCollection, deleteCollection, updateFolder, deleteFolder, createQuery, refreshCollections } = useCollectionsStore();
  const { getSelectedQueryIds, clearSelection } = useQueryLibrarySelectionStore();
  const { user } = useAuthStore();

  // Sharing state
  const [showSharingModal, setShowSharingModal] = useState(false);
  const [isSharedCollection, setIsSharedCollection] = useState(false);
  const [sharedUserCount, setSharedUserCount] = useState<number>(0);
  const [pendingInvitationsCount, setPendingInvitationsCount] = useState<number>(0);
  
  // Check if this is a collection shared with the current user (not owned by them)
  const isSharedWithMe = Boolean(
    collection.owner?.id && 
    user?.id && 
    collection.owner.id !== user.id &&
    (collection.permissionLevel === 'read' || collection.permissionLevel === 'write')
  );

  // Debounce timeout reference
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if collection is shared and get pending invitations count (only if user has permission to view)
  const checkSharingStatus = useCallback(async (collectionId: string) => {
    // Only check sharing status if user is owner or has admin permission
    const isOwner = collection.owner?.id === user?.id;
    const hasAdminPermission = collection.permissionLevel === 'admin';
    
    if (!isOwner && !hasAdminPermission) {
      // Users with read/write permissions cannot view permissions or invitations
      setIsSharedCollection(false);
      setSharedUserCount(0);
      setPendingInvitationsCount(0);
      return;
    }
    
    try {
      // Fetch permissions count
      const permissions = await CollectionsService.getCollectionPermissions({ collectionId });
      const count = permissions.permissions?.length || 0;
      setIsSharedCollection(count > 0);
      setSharedUserCount(count);
      
      // Fetch pending invitations count
      try {
        const invitations = await CollectionSharingService.listCollectionInvitations({
          collectionId,
          status: 'pending'
        });
        setPendingInvitationsCount(invitations.length || 0);
      } catch {
        // If we can't fetch invitations, set to 0
        setPendingInvitationsCount(0);
      }
    } catch {
      // If we can't fetch permissions, assume not shared
      setIsSharedCollection(false);
      setSharedUserCount(0);
      setPendingInvitationsCount(0);
    }
  }, [collection.owner?.id, collection.permissionLevel, user?.id]);

  const fetchThisCollectionDetails = useCallback(async (collectionId: string) => {
    setIsLoadingDetails(true);
    try {
      const { CollectionsService } = await import('../../services/api/services/CollectionsService');
      const details = await CollectionsService.getCollection({ collectionId });
      setCollectionDetails(details);
    } catch (error) {
      console.error('Failed to fetch collection details:', error);
    } finally {
      setIsLoadingDetails(false);
    }
  }, []);

  // Debounced refresh function to prevent multiple simultaneous requests
  const debouncedRefreshCollection = useCallback((collectionId: string) => {
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    // Set new timeout
    refreshTimeoutRef.current = setTimeout(() => {
      void fetchThisCollectionDetails(collectionId);
    }, 100); // 100ms debounce
  }, [fetchThisCollectionDetails]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isExpanded && collection.id && (!collectionDetails || detailsVersion > 0)) {
      void fetchThisCollectionDetails(collection.id);
    }
  }, [isExpanded, collection.id, detailsVersion, collectionDetails, fetchThisCollectionDetails]);

  // Check sharing status when component mounts
  useEffect(() => {
    if (collection.id) {
      void checkSharingStatus(collection.id);
    }
  }, [collection.id, checkSharingStatus]);

  // Subscribe to collection update events
  useEffect(() => {
    if (!collection.id) return undefined;

    const handleQueryUpdate = (event: CustomEvent) => {
      const detail = event.detail as { collectionId?: string };
      if (detail.collectionId === collection.id && collection.id) {
          // Use debounced refresh to prevent multiple simultaneous requests
        debouncedRefreshCollection(collection.id);
      }
    };

    const handleQueryMove = (event: CustomEvent) => {
      // Refresh if this collection is either source or target of the move
      const detail = event.detail as { collectionId?: string; targetCollectionId?: string };
      if (detail.collectionId === collection.id || 
          detail.targetCollectionId === collection.id) {
          // Use debounced refresh instead of immediate version increment
        if (collection.id) {
          debouncedRefreshCollection(collection.id);
        }
      }
    };

    const handleFolderUpdate = (event: CustomEvent) => {
      const detail = event.detail as { collectionId?: string };
      if (detail.collectionId === collection.id && collection.id) {
          // Use debounced refresh to prevent multiple simultaneous requests
        debouncedRefreshCollection(collection.id);
      }
    };

    const unsubscribeUpdate = subscribeToCollectionEvent(COLLECTION_EVENTS.QUERY_UPDATED, handleQueryUpdate);
    const unsubscribeCreate = subscribeToCollectionEvent(COLLECTION_EVENTS.QUERY_CREATED, handleQueryUpdate);
    const unsubscribeDelete = subscribeToCollectionEvent(COLLECTION_EVENTS.QUERY_DELETED, handleQueryUpdate);
    const unsubscribeMove = subscribeToCollectionEvent(COLLECTION_EVENTS.QUERY_MOVED, handleQueryMove);
    const unsubscribeFolderCreate = subscribeToCollectionEvent(COLLECTION_EVENTS.FOLDER_CREATED, handleFolderUpdate);
    const unsubscribeFolderUpdate = subscribeToCollectionEvent(COLLECTION_EVENTS.FOLDER_UPDATED, handleFolderUpdate);
    const unsubscribeFolderDelete = subscribeToCollectionEvent(COLLECTION_EVENTS.FOLDER_DELETED, handleFolderUpdate);

    return () => {
      unsubscribeUpdate();
      unsubscribeCreate();
      unsubscribeDelete();
      unsubscribeMove();
      unsubscribeFolderCreate();
      unsubscribeFolderUpdate();
      unsubscribeFolderDelete();
    };
  }, [collection.id, debouncedRefreshCollection]);


  const collectionQueries = useMemo(() => collectionDetails?.queries || [], [collectionDetails?.queries]);
  const folders = useMemo(() => collectionDetails?.folders || [], [collectionDetails?.folders]);
  const subcollections = useMemo(() => collectionDetails?.subcollections || [], [collectionDetails?.subcollections]);
  const hasQueries = collectionQueries.length > 0;
  const hasFolders = folders.length > 0;
  const hasSubcollections = subcollections.length > 0;
  const hasContent = hasQueries || hasFolders || hasSubcollections;
  
  // Collect all query IDs in tree order for multi-select support
  const allQueryIds = useMemo(() => {
    // If we have parent query IDs, use those (we're in a sub-collection)
    // Otherwise, collect from this collection
    if (parentAllQueryIds && parentAllQueryIds.length > 0) {
      // We already have all query IDs from parent
      return parentAllQueryIds;
    } else if (collectionDetails) {
      // Use the full collection details if available - this includes queries in folders
      const ids = collectAllQueryIds(collectionDetails.queries || [], collectionDetails.folders || []);
      return ids;
    } else {
      // Fallback: collect from what we have locally
      return collectAllQueryIds(collectionQueries, folders);
    }
  }, [parentAllQueryIds, collectionQueries, folders, collectionDetails]);

  const handleToggle = () => {
    if (collection.id) {
      // Collect all nested IDs for cascading collapse
      const subcollectionIds = collectAllSubcollectionIds(subcollections);
      const folderIds = collectAllFolderIds(folders);
      
      toggleCollectionWithChildren(collection.id, subcollectionIds, folderIds);
    }
  };

  const handleQueryClick = useCallback((query: CollectionQuery) => {
    if (collection.id) {
      // Pass the query data we have - QueryEditor will handle fetching fresh data if needed
      onQuerySelect?.({ ...query, collectionId: collection.id });
    }
  }, [collection.id, onQuerySelect]);

  const handleCreateQuery = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (collection.id) {
      onCreateQuery?.(collection.id);
    }
  };

  const handleContextMenu = useCallback((e: React.MouseEvent, type: 'collection' | 'folder' | 'query', item: Collection | Folder | CollectionQuery, collectionId?: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    // For queries: if user has read-only permission, don't show context menu at all
    if (type === 'query' && collection.permissionLevel === 'read') {
      return;
    }
    
    setContextMenu({
      position: { x: e.clientX, y: e.clientY },
      type,
      item,
      collectionId
    });
  }, [collection.permissionLevel]);


  const handleRename = useCallback((type: 'collection' | 'folder' | 'query', item: Collection | Folder | CollectionQuery, collectionId?: string) => {
    setEditModal({ type, item, collectionId });
  }, []);

  const handleDelete = useCallback((type: 'collection' | 'folder' | 'query', item: Collection | Folder | CollectionQuery, collectionId?: string) => {
    const itemName = 'name' in item ? item.name : 'Untitled';
    
    const performDelete = async () => {
      try {
        if (type === 'collection' && 'id' in item && item.id) {
          await deleteCollection(item.id, true);
          toast.success('Collection deleted successfully');
        } else if (type === 'folder' && 'id' in item && item.id && collectionId) {
          const folderName = 'name' in item ? item.name : 'Unknown';
          await deleteFolder(collectionId, item.id, true);
          
          // Force immediate refresh of the collection to remove the deleted folder
          setDetailsVersion(v => v + 1);
          await fetchThisCollectionDetails(collectionId);
          
          // Dispatch event for other components immediately
          dispatchCollectionEvent(COLLECTION_EVENTS.FOLDER_DELETED, {
            collectionId,
            folderId: item.id,
            folderName
          });
          
          // Notify parent to refresh as well
          onRefreshNeeded?.();
          
          toast.success('Folder deleted successfully');
        } else if (type === 'query' && 'id' in item && item.id && collectionId) {
          // Use the API directly with string UUID (fixing API spec mismatch)
          await CollectionsService.deleteCollectionQuery({ 
            collectionId, 
            queryId: item.id as unknown as number // Pass string ID directly, API spec should be string not number
          });
          
          // Refresh the collection to update the UI first
          await fetchThisCollectionDetails(collectionId);
          
          // Give the backend a moment to update stats, then dispatch event for other components
          setTimeout(() => {
            dispatchCollectionEvent(COLLECTION_EVENTS.QUERY_DELETED, {
              collectionId,
              queryId: item.id
            });
          }, 100);
          
          toast.success('Query deleted successfully');
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : `Failed to delete ${type}`;
        toast.error(errorMessage);
      }
    };
    
    showConfirmation(
      `Delete ${type.charAt(0).toUpperCase() + type.slice(1)}`,
      `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
      () => { void performDelete(); },
      true
    );
  }, [deleteCollection, deleteFolder, fetchThisCollectionDetails, onRefreshNeeded, showConfirmation]);

  const handleSaveEdit = useCallback(async (data: string | { name: string; description: string; databaseName?: string; schemaName?: string }) => {
    if (!editModal) return;
    
    const { type, item, collectionId } = editModal;
    
    // For backward compatibility with EditNameModal
    const newName = typeof data === 'string' ? data : data.name;
    // Always pass description when it's provided (including empty string)
    const newDescription = typeof data === 'string' ? undefined : data.description;
    const newDatabaseName = typeof data === 'object' ? data.databaseName : undefined;
    const newSchemaName = typeof data === 'object' ? data.schemaName : undefined;
    
    try {
      if (type === 'collection' && 'id' in item && item.id) {
        const updateData: { name: string; description?: string } = { name: newName };
        if (newDescription !== undefined) {
          updateData.description = newDescription;
        }
        await updateCollection(item.id, updateData);
        toast.success('Collection updated successfully');
      } else if (type === 'folder' && 'id' in item && item.id && collectionId) {
        await updateFolder(collectionId, item.id, { name: newName });
        toast.success('Folder renamed successfully');
      } else if (type === 'query' && 'id' in item && item.id && collectionId) {
        // Use the API directly since the store expects number but we have string UUID
        // Build request body, always include description if it's defined (including empty string)
        const requestBody: { name: string; description?: string; databaseName?: string; schemaName?: string } = { name: newName };
        if (newDescription !== undefined) {
          requestBody.description = newDescription;
        }
        if (newDatabaseName !== undefined) {
          requestBody.databaseName = newDatabaseName;
        }
        if (newSchemaName !== undefined) {
          requestBody.schemaName = newSchemaName;
        }
        
        await CollectionsService.updateCollectionQuery({ 
          collectionId, 
          queryId: item.id as unknown as number, // Pass string ID directly, API spec should be string not number
          requestBody
        });
        // Refresh the collection to update the UI
        await fetchThisCollectionDetails(collectionId);
        toast.success('Query updated successfully');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : `Failed to update ${type}`;
      throw new Error(errorMessage);
    }
  }, [editModal, updateCollection, updateFolder, fetchThisCollectionDetails]);

  // Handle leaving a shared collection
  const handleLeaveCollection = useCallback((collectionId: string, collectionName: string) => {
    showConfirmation(
      'Leave Collection',
      `Are you sure you want to leave "${collectionName}"? You will lose access to this collection.`,
      async () => {
        try {
          await CollectionSharingService.leaveSharedCollection({
            collectionId
          });
          
          toast.success('You have left the collection');
          
          // Refresh collections to remove it from the tree
          await refreshCollections();
          
          // Dispatch event to notify other components
          dispatchCollectionEvent(COLLECTION_EVENTS.COLLECTION_UPDATED, {
            collectionId
          });
        } catch (error: unknown) {
          const errorObj = error as Record<string, unknown>;
          const body = errorObj?.body as Record<string, unknown> | undefined;
          const message = (body?.message as string) || 'Failed to leave collection';
          toast.error(message);
        }
      },
      true
    );
  }, [showConfirmation, refreshCollections]);

  const getContextMenuItems = useCallback((type: 'collection' | 'folder' | 'query', item: Collection | Folder | CollectionQuery, collectionId?: string): ContextMenuItem[] => {
    // For queries: if user has read-only permission, don't show any context menu
    if (type === 'query' && collection.permissionLevel === 'read') {
      return [];
    }
    
    // Check if this is a query and if multiple queries are selected
    const selectedQueryIds = getSelectedQueryIds();
    const isMultiSelect = type === 'query' && selectedQueryIds.length > 1;
    
    if (isMultiSelect) {
      // Show bulk operations for multi-selected queries
      return [
        {
          id: 'delete-selected',
          label: `Delete ${selectedQueryIds.length} selected queries`,
          icon: <TrashIcon className="w-4 h-4" />,
          onClick: () => {
            showConfirmation(
              `Delete Multiple Queries`,
              `Are you sure you want to delete ${selectedQueryIds.length} selected queries? This action cannot be undone.`,
              () => {
                void (async () => {
                  try {
                    // Delete all selected queries
                    const deletePromises = selectedQueryIds.map(queryId => 
                      CollectionsService.deleteCollectionQuery({ 
                        collectionId: collectionId!, 
                        queryId: queryId as unknown as number // Pass string ID directly, API spec should be string not number
                      })
                    );
                    
                    await Promise.all(deletePromises);
                    
                    // Clear selection after deletion
                    clearSelection();
                    
                    // Refresh the collection to update the UI first
                    await fetchThisCollectionDetails(collectionId!);
                    
                    // Give the backend a moment to update stats, then dispatch events for other components
                    setTimeout(() => {
                      for (const queryId of selectedQueryIds) {
                        dispatchCollectionEvent(COLLECTION_EVENTS.QUERY_DELETED, {
                          collectionId: collectionId!,
                          queryId: queryId
                        });
                      }
                    }, 100);
                    
                    toast.success(`Successfully deleted ${selectedQueryIds.length} queries`);
                  } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : `Failed to delete queries`;
                    toast.error(errorMessage);
                  }
                })();
              },
              true
            );
          },
          dangerous: true
        }
      ];
    }
    
    const baseItems: ContextMenuItem[] = [
      {
        id: 'rename',
        label: type === 'query' ? 'Edit query' : `Rename ${type}`,
        icon: <PencilIcon className="w-4 h-4" />,
        onClick: () => handleRename(type, item, collectionId)
      },
      {
        id: 'delete',
        label: `Delete ${type}`,
        icon: <TrashIcon className="w-4 h-4" />,
        onClick: () => { handleDelete(type, item, collectionId); },
        dangerous: true,
        separator: true
      }
    ];

    if (type === 'collection') {
      // Check if current user is the owner of the collection
      const isOwner = collection.owner?.id === user?.id;
      const { permissionLevel } = collection;
      
      // For read-only users who are not the owner, only show "Leave shared collection"
      if (permissionLevel === 'read' && !isOwner) {
        return [
          {
            id: 'leave-collection',
            label: 'Leave Shared Collection',
            icon: <ArrowRightOnRectangleIcon className="w-4 h-4" />,
            onClick: async () => collection.id && handleLeaveCollection(collection.id, collection.name || 'Collection'),
            dangerous: true
          }
        ];
      }
      
      // For owners and users with write/admin permissions, show full menu
      const collectionItems: ContextMenuItem[] = [];
      
      // Only add create actions if user has write or higher permission
      if (permissionLevel !== 'read') {
        collectionItems.push(
          {
            id: 'create-query',
            label: 'Add Query',
            icon: <PlusIcon className="w-4 h-4" />,
            onClick: () => collection.id && onCreateQuery?.(collection.id)
          },
          {
            id: 'create-folder',
            label: 'Create Folder',
            icon: <FolderIcon className="w-4 h-4" />,
            onClick: () => { onCreateFolder?.(collection.id!); },
            separator: true
          }
        );
      }
      
      // Add sharing option if user is the owner or has admin permission
      const hasAdminPermission = collection.permissionLevel === 'admin';
      if (isOwner || hasAdminPermission) {
        collectionItems.push({
          id: 'share-collection',
          label: 'Share Collection',
          icon: <ShareIcon className="w-4 h-4" />,
          onClick: () => setShowSharingModal(true),
          separator: true
        });
      }
      
      // Add "Leave shared collection" option for non-owners with write/admin permissions
      if (!isOwner && permissionLevel !== 'read') {
        collectionItems.push({
          id: 'leave-collection',
          label: 'Leave Shared Collection',
          icon: <ArrowRightOnRectangleIcon className="w-4 h-4" />,
          onClick: async () => collection.id && handleLeaveCollection(collection.id, collection.name || 'Collection'),
          dangerous: true,
          separator: true
        });
      }
      
      // Only show rename/delete for owners or admin users
      const allowModification = isOwner || permissionLevel === 'admin';
      const modificationItems = allowModification ? baseItems : [];
      
      return [
        ...collectionItems,
        ...modificationItems
      ];
    }

    if (type === 'folder') {
      return [
        {
          id: 'create-query',
          label: 'Add Query',
          icon: <PlusIcon className="w-4 h-4" />,
          onClick: () => collectionId && 'id' in item && onCreateQuery?.(collectionId, item.id),
          separator: true
        },
        ...baseItems
      ];
    }

    return baseItems;
  }, [collection.id, collection.owner?.id, collection.permissionLevel, collection.name, user?.id, clearSelection, fetchThisCollectionDetails, getSelectedQueryIds, handleDelete, handleRename, handleLeaveCollection, onCreateQuery, onCreateFolder, showConfirmation]);

  const handleDrop = useCallback(async (item: DragItem, target: DropTarget) => {
      
    try {
      const targetCollectionId = target.collectionId || collection.id;
      const targetFolderId = target.type === 'folder' ? target.id : undefined;

      if (!targetCollectionId) {
        return {
          success: false,
          message: 'Invalid drop target',
          targetType: target.type,
          targetId: target.id
        };
      }

      if (item.type === DragItemTypes.QUERY) {
        
        // Use the API directly with string UUID (fixing API spec mismatch)
        await CollectionsService.moveCollectionQuery({
          collectionId: item.collectionId,
          queryId: item.id as unknown as number, // Pass string ID directly, API spec should be string not number
          requestBody: {
            targetCollectionId,
            targetFolderId
          }
        });
        
        // Dispatch move event to notify all components - let event system handle refreshes
        dispatchCollectionEvent(COLLECTION_EVENTS.QUERY_MOVED, {
          collectionId: item.collectionId,
          queryId: item.id,
          targetCollectionId,
          targetFolderId
        });

        toast.success('Query moved successfully');
        
        return {
          success: true,
          message: 'Query moved successfully',
          targetType: target.type,
          targetId: target.id,
          targetName: target.name
        };
        
      } else if (item.type === DragItemTypes.EDITOR_TAB) {
        
        const queryName = item.label || 'Untitled Query';
        const queryContent = item.content || '';
        
        if (!collection.id) {
          return {
            success: false,
            message: 'Invalid collection',
            targetType: target.type,
            targetId: target.id
          };
        }

        // Create a new query in this collection
        const queryId = await createQuery(collection.id, {
          name: queryName,
          query: queryContent,
          description: `Saved from editor tab: ${queryName}`,
          dataSourceId: item.datasourceId,
          databaseName: item.databaseName,
          schemaName: item.schemaName
        });

        if (queryId) {
          toast.success(`Query "${queryName}" saved to ${collection.name}`);
          
          // Dispatch event to notify other components - let event system handle refreshes
          dispatchCollectionEvent(COLLECTION_EVENTS.QUERY_CREATED, {
            collectionId: collection.id,
            queryId,
            tabId: item.id,
            queryName: queryName
          });
          
          return {
            success: true,
            message: `Query "${queryName}" saved successfully`,
            targetType: target.type,
            targetId: target.id,
            targetName: target.name
          };
        } else {
          return {
            success: false,
            message: 'Failed to create query',
            targetType: target.type,
            targetId: target.id
          };
        }
      } else {
        return {
          success: false,
          message: `Unsupported drag item type: ${item.type}`,
          targetType: target.type,
          targetId: target.id
        };
      }
    } catch (error) {
      console.error('Drop error:', error);
      const message = error instanceof Error ? error.message : 'Drop operation failed';
      toast.error(message);
      
      return {
        success: false,
        message,
        targetType: target.type,
        targetId: target.id
      };
    }
  }, [collection.id, collection.name, createQuery]);



  const paddingLeft = `${level * 0.5}rem`;

  // Memoize styles and target objects to avoid creating new objects on each render
  const paddingStyle = useMemo(() => ({ paddingLeft }), [paddingLeft]);
  
  const dropZoneTarget = useMemo(() => ({
    type: 'collection' as const,
    id: collection.id || '',
    collectionId: collection.id || '',
    acceptedTypes: [DragItemTypes.QUERY, DragItemTypes.EDITOR_TAB],
    name: collection.name
  }), [collection.id, collection.name]);

  // Memoize callback functions
  const handleCollectionContextMenu = useCallback((e: React.MouseEvent) => {
    handleContextMenu(e, 'collection', collection);
  }, [handleContextMenu, collection]);

  const handleRefreshNeeded = useCallback(() => {
    setDetailsVersion(v => v + 1);
  }, []);

  const emptyQueryArray = useMemo(() => [], []);

  // Create memoized callback for folder refresh
  const handleFolderRefreshNeeded = useCallback(() => {
    setDetailsVersion(v => v + 1);
  }, []);

  // Create memoized style for query items
  const queryItemStyle = useMemo(() => ({ 
    paddingLeft: `${(level + 1) * 0.5}rem` 
  }), [level]);

  // Create memoized style for empty state
  const emptyStateStyle = useMemo(() => ({ 
    paddingLeft: `${(level + 1) * 0.5}rem` 
  }), [level]);

  // Memoize context menu items to avoid creating new arrays on each render
  const contextMenuItems = useMemo(() => {
    if (!contextMenu) return [];
    return getContextMenuItems(contextMenu.type, contextMenu.item, contextMenu.collectionId);
  }, [contextMenu, getContextMenuItems]);

  // Memoize subcollections to avoid creating new objects on each render
  const memoizedSubcollections = useMemo(() => 
    subcollections.map((subcollection: Collection) => ({
      ...subcollection,
      isPublic: Boolean(subcollection.isPublic)
    })), [subcollections]);

  // Memoize context menu close handler
  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Memoize edit modal close handler
  const handleCloseEditModal = useCallback(() => {
    setEditModal(null);
  }, []);

  // Memoize query handlers factory
  const createQueryHandlers = useCallback((query: CollectionQuery) => ({
    onClick: () => handleQueryClick(query),
    onContextMenu: (e: React.MouseEvent) => handleContextMenu(e, 'query', query, collection.id)
  }), [handleQueryClick, handleContextMenu, collection.id]);


  // Memoize add first query handler
  const handleAddFirstQuery = useCallback(() => {
    if (collection.id) {
      onCreateQuery?.(collection.id);
    }
  }, [collection.id, onCreateQuery]);

  return (
    <div className="select-none">
      {/* Collection Header */}
      <DropZone
        target={dropZoneTarget}
        onDrop={handleDrop}
        className="w-full"
      >
        <div className="relative">
          <div
            className={`flex items-center py-1.5 px-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer group`}
            style={paddingStyle}
            onClick={handleToggle}
            onContextMenu={handleCollectionContextMenu}
          >
        {/* Expand/Collapse Icon */}
        <div className="flex-shrink-0 w-3 h-3 mr-1">
          {hasContent && (
            isExpanded ? (
              <ChevronDownIcon className="w-3 h-3 text-gray-400" />
            ) : (
              <ChevronRightIcon className="w-3 h-3 text-gray-400" />
            )
          )}
        </div>

        {/* Collection Icon */}
        <div className="flex-shrink-0 mr-2">
          {isExpanded ? (
            <FolderOpenIcon className="w-4 h-4 text-blue-500" />
          ) : (
            <FolderIcon className="w-4 h-4 text-blue-500" />
          )}
        </div>

        {/* Collection Name */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {collection.name}
            </span>
            {/* Show indicator for shared collections or pending invitations */}
            {(isSharedCollection || isSharedWithMe || (pendingInvitationsCount > 0 && (collection.owner?.id === user?.id || collection.permissionLevel === 'admin'))) && (
              <div className="flex items-center ml-1.5">
                <UsersIcon 
                  className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 flex-shrink-0" 
                  title={
                    isSharedWithMe 
                      ? "Shared with you" 
                      : (pendingInvitationsCount > 0 && sharedUserCount === 0)
                        ? "Pending invitations"
                        : "Shared with others"
                  }
                />
                {/* Show counts only for admin/owner users */}
                {(collection.owner?.id === user?.id || collection.permissionLevel === 'admin') && (
                  <div className="flex items-center">
                    {/* Permissions count - only show if there are shared users */}
                    {sharedUserCount > 0 && (
                      <span className="ml-0.5 text-xs text-blue-500 dark:text-blue-400" title="Shared users">
                        ({sharedUserCount})
                      </span>
                    )}
                    {/* Pending invitations count - always show if there are pending invitations */}
                    {pendingInvitationsCount > 0 && (
                      <span className="ml-0.5 text-xs text-amber-500 dark:text-amber-400" title="Pending invitations">
                        (+{pendingInvitationsCount})
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          {collection.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
              {collection.description}
            </p>
          )}
        </div>

        {/* Actions */}
        {/* Only show actions if user has write or higher permission */}
        {collection.permissionLevel !== 'read' && (
          <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button type="button"
              onClick={handleCreateQuery}
              className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              title="Add Query"
            >
              <PlusIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
          </div>
        </div>
      </DropZone>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-l border-gray-200 dark:border-gray-700 ml-3">
          {/* Subcollections */}
          {memoizedSubcollections.map((subcollection: Collection) => (
              <TreeNode
                key={subcollection.id}
                collection={subcollection}
                queries={emptyQueryArray}
                level={level + 1}
                onQuerySelect={onQuerySelect}
                onCreateQuery={onCreateQuery}
                onCreateFolder={onCreateFolder}
                selectedQueryId={selectedQueryId}
                onRefreshNeeded={handleRefreshNeeded}
                parentAllQueryIds={allQueryIds}
              />
          ))}

          {/* Folders */}
          {folders.map((folder: Folder) => {
            const folderQueries = collectionQueries.filter((q: CollectionQuery) => q.folderId === folder.id);
            return (
              <FolderNode
                key={folder.id}
                folder={folder}
                collectionId={collection.id || ''}
                queries={folderQueries}
                subfolders={folders.filter((f: Folder) => f.parentFolderId === folder.id)}
                level={level + 1}
                onQuerySelect={onQuerySelect}
                onCreateQuery={onCreateQuery}
                selectedQueryId={selectedQueryId}
                onRefreshNeeded={handleFolderRefreshNeeded}
                allQueryIds={allQueryIds}
                collectionPermissionLevel={collection.permissionLevel}
              />
            );
          })}

          {/* Root Queries (not in any folder) */}
          {collectionQueries.filter((q: CollectionQuery) => !q.folderId).map((query: CollectionQuery) => {
            const queryHandlers = createQueryHandlers(query);
            
            return (
              <DraggableQuery
                key={query.id}
                query={query}
                collectionId={collection.id || ''}
                folderId={undefined}
                isSelected={selectedQueryId === query.id}
                onClick={queryHandlers.onClick}
                onContextMenu={queryHandlers.onContextMenu}
                style={queryItemStyle}
                allQueryIds={allQueryIds}
              />
            );
          })}

          {/* Empty State */}
          {!hasContent && (
            <div className="py-4 px-3 text-center" style={emptyStateStyle}>
              <p className="text-sm text-gray-500 dark:text-gray-400">No content yet</p>
              <button type="button"
                onClick={handleAddFirstQuery}
                className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
              >
                Add your first query
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Context Menu */}
      <ContextMenu
        items={contextMenuItems}
        position={contextMenu?.position || null}
        onClose={handleCloseContextMenu}
      />
      
      {/* Edit Modal */}
      {editModal?.type === 'query' ? (
        (() => {
          // Debug logging and get datasource
          console.log('[CollectionTree] Edit query modal - editModal.item:', editModal.item);
          const queryDataSourceId = 'dataSourceId' in editModal.item ? editModal.item.dataSourceId : undefined;
          console.log('[CollectionTree] Query dataSourceId:', queryDataSourceId);
          
          // If no datasource ID on the query, try to get the default/active datasource
          let finalDataSourceId = queryDataSourceId;
          if (!finalDataSourceId) {
            // Try to get from connection store or first available datasource
            const connectionStore = useConnectionStore.getState();
            if (connectionStore.activeConnection?.id) {
              finalDataSourceId = connectionStore.activeConnection.id;
              console.log('[CollectionTree] Using active connection ID:', finalDataSourceId);
            }
          }
          
          return (
            <EditQueryModal
              isOpen={!!editModal}
              onClose={handleCloseEditModal}
              onSave={handleSaveEdit}
              title="Edit Query"
              currentName={'name' in editModal.item ? editModal.item.name || '' : ''}
              currentDescription={'description' in editModal.item ? editModal.item.description : undefined}
              currentDatabaseName={'databaseName' in editModal.item ? editModal.item.databaseName : undefined}
              currentSchemaName={'schemaName' in editModal.item ? editModal.item.schemaName : undefined}
              dataSourceId={finalDataSourceId}
              dataSourceType={(() => {
                if (finalDataSourceId) {
                  const dataSourceStore = useDataSourceStore.getState();
                  // Fetch datasource if not already loaded
                  if (!dataSourceStore.dataSources.has(finalDataSourceId)) {
                    dataSourceStore.fetchDataSource(finalDataSourceId);
                  }
                  const dataSource = dataSourceStore.dataSources.get(finalDataSourceId);
                  return dataSource?.databaseType?.toLowerCase();
                }
                return undefined;
              })()}
              namePlaceholder="Enter query name"
              descriptionPlaceholder="Enter query description (optional)"
              maxNameLength={70}
              maxDescriptionLength={100}
              showDatabaseSelection={true}
            />
          );
        })()
      ) : editModal?.type === 'collection' ? (
        <EditQueryModal
          isOpen={!!editModal}
          onClose={handleCloseEditModal}
          onSave={handleSaveEdit}
          title="Edit Collection"
          currentName={'name' in editModal.item ? editModal.item.name || '' : ''}
          currentDescription={'description' in editModal.item ? editModal.item.description : undefined}
          namePlaceholder="Enter collection name"
          descriptionPlaceholder="Enter collection description (optional)"
          maxNameLength={70}
          maxDescriptionLength={100}
        />
      ) : (
        editModal && (
          <EditNameModal
            isOpen={true}
            onClose={handleCloseEditModal}
            onSave={handleSaveEdit}
            title={`Rename ${editModal.type}`}
            currentName={'name' in editModal.item ? editModal.item.name || '' : ''}
            placeholder={`Enter ${editModal.type} name`}
          />
        )
      )}
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmation}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.isDangerous ? 'Delete' : 'OK'}
        cancelText="Cancel"
        variant={confirmationModal.isDangerous ? 'danger' : 'warning'}
        isDangerous={confirmationModal.isDangerous}
      />

      {/* Collection Sharing Modal */}
      {collection.id && (
        <CollectionSharingModal
          collectionId={collection.id}
          collectionName={collection.name || 'Untitled Collection'}
          isOpen={showSharingModal}
          onClose={() => { setShowSharingModal(false) }}
          onInviteSent={() => {
            // Refresh sharing status and pending invitations count
            if (collection.id) {
              void checkSharingStatus(collection.id);
            }
          }}
        />
      )}
    </div>
  );
};

const FolderNode: React.FC<FolderNodeProps> = ({
  folder,
  collectionId,
  queries: initialQueries,
  subfolders: initialSubfolders,
  level,
  onQuerySelect,
  onCreateQuery,
  selectedQueryId,
  onRefreshNeeded,
  allQueryIds,
  collectionPermissionLevel
}) => {
  const { isFolderExpanded, toggleFolderWithChildren } = useCollectionTreeStateStore();
  const isExpanded = folder.id ? isFolderExpanded(folder.id) : false;
  const [contextMenu, setContextMenu] = useState<ContextMenuState>(null);
  const [editModal, setEditModal] = useState<EditModalState>(null);
  const { confirmationModal, showConfirmation, closeConfirmation } = useConfirmationModal();
  const [folderDetails, setFolderDetails] = useState<{
    queries?: CollectionQuery[];
    subfolders?: Folder[];
  } | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailsVersion, setDetailsVersion] = useState(0); // Force re-fetch when incremented
  const { updateFolder, deleteFolder, createQuery } = useCollectionsStore();
  const { getSelectedQueryIds, clearSelection } = useQueryLibrarySelectionStore();
  
  // Debounce timeout reference for folder
  const folderRefreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch folder details when expanded
  const fetchFolderDetails = useCallback(async () => {
    if (!folder.id || isLoadingDetails) return;
    
    setIsLoadingDetails(true);
    try {
      const { CollectionsService } = await import('../../services/api/services/CollectionsService');
      const details = await CollectionsService.getFolder({ 
        collectionId, 
        folderId: folder.id 
      });
      setFolderDetails(details);
    } catch (error) {
      console.error('Failed to fetch folder details:', error);
    } finally {
      setIsLoadingDetails(false);
    }
  }, [folder.id, isLoadingDetails, collectionId]);

  // Debounced refresh function to prevent multiple simultaneous requests
  const debouncedRefreshFolder = useCallback(() => {
    // Clear any existing timeout
    if (folderRefreshTimeoutRef.current) {
      clearTimeout(folderRefreshTimeoutRef.current);
    }
    
    // Set new timeout
    folderRefreshTimeoutRef.current = setTimeout(() => {
      void fetchFolderDetails();
    }, 100); // 100ms debounce
  }, [fetchFolderDetails]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (folderRefreshTimeoutRef.current) {
        clearTimeout(folderRefreshTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isExpanded && (!folderDetails || detailsVersion > 0)) {
      void fetchFolderDetails();
    }
  }, [isExpanded, detailsVersion, fetchFolderDetails, folderDetails]);

  // Subscribe to collection update events
  useEffect(() => {
    if (!collectionId || !folder.id) return undefined;

    const handleQueryUpdate = (event: CustomEvent) => {
      const detailUnknown = event?.detail as unknown;
      const detail = (detailUnknown && typeof detailUnknown === 'object' && !Array.isArray(detailUnknown)) ? detailUnknown as { collectionId?: unknown } : {};
      const cid = typeof detail.collectionId === 'string' ? detail.collectionId : undefined;
      if (cid === collectionId) {
          // Use debounced refresh to prevent multiple simultaneous requests
        debouncedRefreshFolder();
      }
    };

    const handleQueryMove = (event: CustomEvent) => {
      // Refresh if this folder is affected by the move
      // Check if move is from/to this collection and potentially this folder
      const detailUnknown = event?.detail as unknown;
      const detail = (detailUnknown && typeof detailUnknown === 'object' && !Array.isArray(detailUnknown)) ? detailUnknown as { collectionId?: unknown; targetCollectionId?: unknown; targetFolderId?: unknown } : {};
      const src = typeof detail.collectionId === 'string' ? detail.collectionId : undefined;
      const tgt = typeof detail.targetCollectionId === 'string' ? detail.targetCollectionId : undefined;
      const tgtFolder = typeof detail.targetFolderId === 'string' ? detail.targetFolderId : undefined;
      const isSourceCollection = src === collectionId;
      const isTargetCollection = tgt === collectionId;
      const isTargetFolder = tgtFolder === folder.id;
      
      // Need to refresh if:
      // 1. Query moved FROM this folder (will be detected by collection refresh)
      // 2. Query moved TO this folder
      if (isSourceCollection || (isTargetCollection && isTargetFolder)) {
          // Use debounced refresh to prevent multiple simultaneous requests
        debouncedRefreshFolder();
      }
    };

    const unsubscribeUpdate = subscribeToCollectionEvent(COLLECTION_EVENTS.QUERY_UPDATED, handleQueryUpdate);
    const unsubscribeCreate = subscribeToCollectionEvent(COLLECTION_EVENTS.QUERY_CREATED, handleQueryUpdate);
    const unsubscribeDelete = subscribeToCollectionEvent(COLLECTION_EVENTS.QUERY_DELETED, handleQueryUpdate);
    const unsubscribeMove = subscribeToCollectionEvent(COLLECTION_EVENTS.QUERY_MOVED, handleQueryMove);

    return () => {
      unsubscribeUpdate();
      unsubscribeCreate();
      unsubscribeDelete();
      unsubscribeMove();
    };
  }, [collectionId, folder.id, debouncedRefreshFolder]);

  // Use folder details if available, otherwise use initial data
  const queries = folderDetails?.queries || initialQueries;
  const subfolders = folderDetails?.subfolders || initialSubfolders;
  
  const hasQueries = queries.length > 0;
  const hasSubfolders = subfolders.length > 0;
  const hasContent = hasQueries || hasSubfolders;
  
  // Combine parent query IDs with queries from this folder
  const folderAllQueryIds = useMemo(() => {
    const folderQueryIds = queries
      .map((q: CollectionQuery) => q.id)
      .filter((id): id is string => typeof id === 'string' && id.length > 0);
    // If we have parent IDs, combine them; otherwise just use folder queries
    if (allQueryIds.length > 0) {
      // Make sure folder queries are included
      const combined = Array.from(new Set([...allQueryIds, ...folderQueryIds]));
      return combined;
    }
    return folderQueryIds;
  }, [allQueryIds, queries]);
  

  const handleToggle = () => {
    if (folder.id) {
      // Collect all nested subfolder IDs for cascading collapse
      const subfolderIds = collectAllSubfolderIds(subfolders);
      
      toggleFolderWithChildren(folder.id, subfolderIds);
    }
  };

  const handleQueryClick = useCallback((query: CollectionQuery) => {
    // Pass the query data we have - QueryEditor will handle fetching fresh data if needed
    onQuerySelect?.({ ...query, collectionId });
  }, [collectionId, onQuerySelect]);

  const handleCreateQuery = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCreateQuery?.(collectionId, folder.id);
  };

  const handleContextMenu = useCallback((e: React.MouseEvent, type: 'folder' | 'query', item: Folder | CollectionQuery) => {
    e.preventDefault();
    e.stopPropagation();
    
    // For queries and folders: if user has read-only permission, don't show context menu at all
    if ((type === 'query' || type === 'folder') && collectionPermissionLevel === 'read') {
      return;
    }
    
    setContextMenu({
      position: { x: e.clientX, y: e.clientY },
      type,
      item,
      collectionId
    });
  }, [collectionId, collectionPermissionLevel]);

  const handleRename = useCallback((type: 'folder' | 'query', item: Folder | CollectionQuery) => {
    setEditModal({ type, item, collectionId });
  }, [collectionId]);

  const handleSaveEdit = useCallback(async (data: string | { name: string; description: string; databaseName?: string; schemaName?: string }) => {
    if (!editModal) return;
    
    const { type, item } = editModal;
    
    // For backward compatibility with EditNameModal
    const newName = typeof data === 'string' ? data : data.name;
    // Always pass description when it's provided (including empty string)
    const newDescription = typeof data === 'string' ? undefined : data.description;
    const newDatabaseName = typeof data === 'object' ? data.databaseName : undefined;
    const newSchemaName = typeof data === 'object' ? data.schemaName : undefined;
    
    try {
      if (type === 'folder' && 'id' in item && item.id) {
        await updateFolder(collectionId, item.id, { name: newName });
        toast.success('Folder renamed successfully');
      } else if (type === 'query' && 'id' in item && item.id) {
        // Use the API directly since the store expects number but we have string UUID
        // Build request body, always include description if it's defined (including empty string)
        const requestBody: { name: string; description?: string; databaseName?: string; schemaName?: string } = { name: newName };
        if (newDescription !== undefined) {
          requestBody.description = newDescription;
        }
        if (newDatabaseName !== undefined) {
          requestBody.databaseName = newDatabaseName;
        }
        if (newSchemaName !== undefined) {
          requestBody.schemaName = newSchemaName;
        }
        
        {
          const queryIdNum = typeof item.id === 'number' ? item.id : Number.parseInt(String(item.id), 10);
          await CollectionsService.updateCollectionQuery({ 
            collectionId, 
            queryId: queryIdNum,
            requestBody
          });
        }
        toast.success('Query updated successfully');
        // Refresh folder details to show updated query
        await fetchFolderDetails();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : `Failed to update ${type}`;
      throw new Error(errorMessage);
    }
  }, [editModal, collectionId, updateFolder, fetchFolderDetails]);


  const paddingLeft = `${level * 0.5}rem`;

  // Memoize styles and objects for FolderNode
  const paddingStyle = useMemo(() => ({ paddingLeft }), [paddingLeft]);
  
  const folderDropZoneTarget = useMemo(() => ({
    type: 'folder' as const,
    id: folder.id || '',
    collectionId: collectionId,
    acceptedTypes: [DragItemTypes.QUERY, DragItemTypes.EDITOR_TAB],
    name: folder.name
  }), [folder.id, folder.name, collectionId]);

  const handleFolderContextMenu = useCallback((e: React.MouseEvent) => {
    handleContextMenu(e, 'folder', folder);
  }, [handleContextMenu, folder]);

  const handleRefreshNeeded = useCallback(() => {
    setDetailsVersion(v => v + 1);
  }, []);

  const folderQueryStyle = useMemo(() => ({ 
    paddingLeft: `${(level + 1) * 0.5}rem` 
  }), [level]);

  // Create memoized style for empty state
  const emptyStateStyle = useMemo(() => ({ 
    paddingLeft: `${(level + 1) * 0.5}rem` 
  }), [level]);

  // Memoize context menu close handler for FolderNode
  const handleCloseFolderContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Memoize edit modal close handler for FolderNode
  const handleCloseEditModal = useCallback(() => {
    setEditModal(null);
  }, []);

  // Need to handle the delete function for FolderNode
  const handleFolderDelete = useCallback((type: 'folder' | 'query', item: Folder | CollectionQuery) => {
    const itemName = 'name' in item ? item.name : 'Untitled';
    
    const performDelete = async () => {
      try {
        if (type === 'folder' && 'id' in item && item.id) {
          await deleteFolder(collectionId, item.id, true);
          
          // Force immediate UI update
          setDetailsVersion(v => v + 1);
          
          // Clear folder details if we deleted ourselves
          if (item.id === folder.id) {
            setFolderDetails(null);
          }
          
          // Notify parent to refresh collection
          onRefreshNeeded?.();
          
          toast.success('Folder deleted successfully');
        } else if (type === 'query' && 'id' in item && item.id) {
          // Use the API directly with string UUID (fixing API spec mismatch)
          {
            const queryIdNum = typeof item.id === 'number' ? item.id : Number.parseInt(String(item.id), 10);
            await CollectionsService.deleteCollectionQuery({ 
              collectionId, 
              queryId: queryIdNum
            });
          }
          
          toast.success('Query deleted successfully');
          // Refresh folder details to remove deleted query
          await fetchFolderDetails();
          
          // Give the backend a moment to update stats, then dispatch event for other components
          setTimeout(() => {
            dispatchCollectionEvent(COLLECTION_EVENTS.QUERY_DELETED, {
              collectionId,
              queryId: item.id
            });
          }, 100);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : `Failed to delete ${type}`;
        toast.error(errorMessage);
      }
    };
    
    showConfirmation(
      `Delete ${type.charAt(0).toUpperCase() + type.slice(1)}`,
      `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
      () => { void performDelete(); },
      true
    );
  }, [collectionId, deleteFolder, fetchFolderDetails, folder.id, onRefreshNeeded, showConfirmation]);

  const handleDelete = handleFolderDelete;

  const getContextMenuItems = useCallback((type: 'folder' | 'query', item: Folder | CollectionQuery): ContextMenuItem[] => {
    // For queries and folders: if user has read-only permission, don't show any context menu
    if ((type === 'query' || type === 'folder') && collectionPermissionLevel === 'read') {
      return [];
    }
    
    // Check if this is a query and if multiple queries are selected
    const selectedQueryIds = getSelectedQueryIds();
    const isMultiSelect = type === 'query' && selectedQueryIds.length > 1;
    
    if (isMultiSelect) {
      // Show bulk operations for multi-selected queries
      return [
        {
          id: 'delete-selected',
          label: `Delete ${selectedQueryIds.length} selected queries`,
          icon: <TrashIcon className="w-4 h-4" />,
          onClick: () => {
            showConfirmation(
              `Delete Multiple Queries`,
              `Are you sure you want to delete ${selectedQueryIds.length} selected queries? This action cannot be undone.`,
              () => {
                void (async () => {
                  try {
                    // Delete all selected queries
                    const deletePromises = selectedQueryIds
                      .map((queryId) => (typeof queryId === 'number' ? queryId : Number.parseInt(String(queryId), 10)))
                      .filter((n) => Number.isFinite(n))
                      .map((n) => CollectionsService.deleteCollectionQuery({ collectionId, queryId: n }));
                    
                    await Promise.all(deletePromises);
                    
                    // Clear selection after deletion
                    clearSelection();
                    
                    // Refresh folder details to update the UI
                    await fetchFolderDetails();
                    
                    // Give the backend a moment to update stats, then dispatch events for other components
                    setTimeout(() => {
                      for (const queryId of selectedQueryIds) {
                        dispatchCollectionEvent(COLLECTION_EVENTS.QUERY_DELETED, {
                          collectionId,
                          queryId: queryId
                        });
                      }
                    }, 100);
                    
                    toast.success(`Successfully deleted ${selectedQueryIds.length} queries`);
                  } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : `Failed to delete queries`;
                    toast.error(errorMessage);
                  }
                })();
              },
              true
            );
          },
          dangerous: true
        }
      ];
    }
    
    const baseItems: ContextMenuItem[] = [
      {
        id: 'rename',
        label: type === 'query' ? 'Edit query' : `Rename ${type}`,
        icon: <PencilIcon className="w-4 h-4" />,
        onClick: () => { handleRename(type, item) }
      },
      {
        id: 'delete',
        label: `Delete ${type}`,
        icon: <TrashIcon className="w-4 h-4" />,
        onClick: () => { handleDelete(type, item); },
        dangerous: true,
        separator: true
      }
    ];

    if (type === 'folder') {
      return [
        {
          id: 'create-query',
          label: 'Add Query',
          icon: <PlusIcon className="w-4 h-4" />,
          onClick: () => onCreateQuery?.(collectionId, folder.id),
          separator: true
        },
        ...baseItems
      ];
    }

    return baseItems;
  }, [clearSelection, collectionId, collectionPermissionLevel, fetchFolderDetails, folder.id, getSelectedQueryIds, handleDelete, handleRename, onCreateQuery, showConfirmation]);

  // Memoize context menu items for FolderNode
  const folderContextMenuItems = useMemo(() => {
    if (!contextMenu) return [];
    const type: 'folder' | 'query' = contextMenu.type === 'folder' ? 'folder' : 'query'
    const item = contextMenu.item as Folder | CollectionQuery
    return getContextMenuItems(type, item);
  }, [contextMenu, getContextMenuItems]);

  // Memoize folder query handlers factory
  const createFolderQueryHandlers = useCallback((query: CollectionQuery) => ({
    onClick: () => { handleQueryClick(query) },
    onContextMenu: (e: React.MouseEvent) => { handleContextMenu(e, 'query', query) }
  }), [handleQueryClick, handleContextMenu]);


  // Memoize add query to folder handler
  const handleAddQueryToFolder = useCallback(() => {
    onCreateQuery?.(collectionId, folder.id);
  }, [collectionId, folder.id, onCreateQuery]);

  const handleDrop = useCallback(async (item: DragItem, target: DropTarget) => {
    
    try {
      if (item.type === DragItemTypes.QUERY) {
        
        // Use the API directly with string UUID (fixing API spec mismatch)
        await CollectionsService.moveCollectionQuery({
          collectionId: item.collectionId,
          // API expects number; best effort parse, fallback to NaN which backend should reject clearly
          queryId: typeof item.id === 'number' ? item.id : Number.parseInt(String(item.id), 10),
          requestBody: {
            targetCollectionId: collectionId,
            targetFolderId: folder.id
          }
        });
        
        // Dispatch move event to notify all components - let event system handle refreshes
        dispatchCollectionEvent(COLLECTION_EVENTS.QUERY_MOVED, {
          collectionId: item.collectionId,
          queryId: item.id,
          targetCollectionId: collectionId,
          targetFolderId: folder.id
        });
        
        toast.success('Query moved successfully');
        
        return {
          success: true,
          message: 'Query moved successfully',
          targetType: target.type,
          targetId: target.id,
          targetName: target.name
        };
        
      } else if (item.type === DragItemTypes.EDITOR_TAB) {
        
        const queryName = item.label || 'Untitled Query';
        const queryContent = item.content || '';
        
        // Create a new query in this folder
        const queryId = await createQuery(collectionId, {
          name: queryName,
          query: queryContent,
          description: `Saved from editor tab: ${queryName}`,
          folderId: folder.id,
          dataSourceId: item.datasourceId,
          databaseName: item.databaseName,
          schemaName: item.schemaName
        });

        if (queryId) {
          toast.success(`Query "${queryName}" saved to ${folder.name}`);
          
          // Dispatch event to notify other components - let event system handle refreshes
          dispatchCollectionEvent(COLLECTION_EVENTS.QUERY_CREATED, {
            collectionId,
            queryId,
            tabId: item.id,
            queryName: queryName
          });
          
          return {
            success: true,
            message: `Query "${queryName}" saved successfully`,
            targetType: target.type,
            targetId: target.id,
            targetName: target.name
          };
        } else {
          return {
            success: false,
            message: 'Failed to create query',
            targetType: target.type,
            targetId: target.id
          };
        }
      } else {
        return {
          success: false,
          message: `Unsupported drag item type: ${item.type}`,
          targetType: target.type,
          targetId: target.id
        };
      }
    } catch (error) {
      console.error('Folder drop error:', error);
      const message = error instanceof Error ? error.message : 'Drop operation failed';
      toast.error(message);
      
      return {
        success: false,
        message,
        targetType: target.type,
        targetId: target.id
      };
    }
  }, [collectionId, folder.id, folder.name, createQuery]);



  return (
    <div className="select-none">
      {/* Folder Header */}
      <DropZone
        target={folderDropZoneTarget}
        onDrop={handleDrop}
        className="w-full"
      >
        <div className="relative">
          <div
            className={`flex items-center py-1.5 px-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer group`}
            style={paddingStyle}
            onClick={handleToggle}
            onContextMenu={handleFolderContextMenu}
          >
        {/* Expand/Collapse Icon */}
        <div className="flex-shrink-0 w-3 h-3 mr-1">
          {hasContent && (
            isExpanded ? (
              <ChevronDownIcon className="w-3 h-3 text-gray-400" />
            ) : (
              <ChevronRightIcon className="w-3 h-3 text-gray-400" />
            )
          )}
        </div>

        {/* Folder Icon */}
        <div className="flex-shrink-0 mr-3">
          {isExpanded ? (
            <FolderOpenIcon className="w-4 h-4 text-yellow-500" />
          ) : (
            <FolderIcon className="w-4 h-4 text-yellow-500" />
          )}
        </div>

        {/* Folder Name */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {folder.name}
            </span>
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
              ({folder.queryCount || 0})
            </span>
          </div>
          {folder.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
              {folder.description}
            </p>
          )}
        </div>

        {/* Actions */}
        {/* Only show actions if user has write or higher permission */}
        {collectionPermissionLevel !== 'read' && (
          <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button type="button"
              onClick={handleCreateQuery}
              className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              title="Add Query"
            >
              <PlusIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
          </div>
        </div>
      </DropZone>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-l border-gray-200 dark:border-gray-700 ml-3">
          {/* Subfolders */}
          {subfolders.map((subfolder: Folder) => (
            <FolderNode
              key={subfolder.id}
              folder={subfolder}
              collectionId={collectionId}
              queries={queries.filter((q: CollectionQuery) => q.folderId === subfolder.id)}
              subfolders={subfolders.filter((f: Folder) => f.parentFolderId === subfolder.id)}
              level={level + 1}
              onQuerySelect={onQuerySelect}
              onCreateQuery={onCreateQuery}
              selectedQueryId={selectedQueryId}
              onRefreshNeeded={handleRefreshNeeded}
              allQueryIds={folderAllQueryIds}
              collectionPermissionLevel={collectionPermissionLevel}
            />
          ))}

          {/* Queries in this folder */}
          {queries.map((query: CollectionQuery) => {
            const queryHandlers = createFolderQueryHandlers(query);
            
            return (
              <DraggableQuery
                key={query.id}
                query={query}
                collectionId={collectionId}
                folderId={folder.id}
                isSelected={selectedQueryId === query.id}
                onClick={queryHandlers.onClick}
                onContextMenu={queryHandlers.onContextMenu}
                style={folderQueryStyle}
                allQueryIds={folderAllQueryIds}
              />
            );
          })}

          {/* Empty State */}
          {!hasContent && (
            <div className="py-4 px-3 text-center" style={emptyStateStyle}>
              <p className="text-sm text-gray-500 dark:text-gray-400">Empty folder</p>
              <button type="button"
                onClick={handleAddQueryToFolder}
                className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
              >
                Add query
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Context Menu */}
      <ContextMenu
        items={folderContextMenuItems}
        position={contextMenu?.position || null}
        onClose={handleCloseFolderContextMenu}
      />
      
      {/* Edit Modal */}
      {editModal?.type === 'query' ? (
        (() => {
          // Debug logging and get datasource
          console.log('[CollectionTree] Edit query modal - editModal.item:', editModal.item);
          const queryDataSourceId = 'dataSourceId' in editModal.item ? editModal.item.dataSourceId : undefined;
          console.log('[CollectionTree] Query dataSourceId:', queryDataSourceId);
          
          // If no datasource ID on the query, try to get the default/active datasource
          let finalDataSourceId = queryDataSourceId;
          if (!finalDataSourceId) {
            // Try to get from connection store or first available datasource
            const connectionStore = useConnectionStore.getState();
            if (connectionStore.activeConnection?.id) {
              finalDataSourceId = connectionStore.activeConnection.id;
              console.log('[CollectionTree] Using active connection ID:', finalDataSourceId);
            }
          }
          
          return (
            <EditQueryModal
              isOpen={!!editModal}
              onClose={handleCloseEditModal}
              onSave={handleSaveEdit}
              title="Edit Query"
              currentName={'name' in editModal.item ? editModal.item.name || '' : ''}
              currentDescription={'description' in editModal.item ? editModal.item.description : undefined}
              currentDatabaseName={'databaseName' in editModal.item ? editModal.item.databaseName : undefined}
              currentSchemaName={'schemaName' in editModal.item ? editModal.item.schemaName : undefined}
              dataSourceId={finalDataSourceId}
              dataSourceType={(() => {
                if (finalDataSourceId) {
                  const dataSourceStore = useDataSourceStore.getState();
                  // Fetch datasource if not already loaded
                  if (!dataSourceStore.dataSources.has(finalDataSourceId)) {
                    dataSourceStore.fetchDataSource(finalDataSourceId);
                  }
                  const dataSource = dataSourceStore.dataSources.get(finalDataSourceId);
                  return dataSource?.databaseType?.toLowerCase();
                }
                return undefined;
              })()}
              namePlaceholder="Enter query name"
              descriptionPlaceholder="Enter query description (optional)"
              maxNameLength={70}
              maxDescriptionLength={100}
              showDatabaseSelection={true}
            />
          );
        })()
      ) : (
        editModal && (
          <EditNameModal
            isOpen={true}
            onClose={handleCloseEditModal}
            onSave={handleSaveEdit}
            title={`Rename ${editModal.type}`}
            currentName={'name' in editModal.item ? editModal.item.name || '' : ''}
            placeholder={`Enter ${editModal.type} name`}
          />
        )
      )}
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmation}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.isDangerous ? 'Delete' : 'OK'}
        cancelText="Cancel"
        variant={confirmationModal.isDangerous ? 'danger' : 'warning'}
        isDangerous={confirmationModal.isDangerous}
      />
    </div>
  );
};

export const CollectionTree: React.FC<CollectionTreeProps> = ({
  onQuerySelect,
  onCreateCollection,
  onCreateQuery,
  onCreateFolder,
  selectedQueryId,
  className = ''
}) => {
  const { collections, isLoading, error, fetchCollections, refreshCollections } = useCollectionsStore();
  const { clearSelection } = useQueryLibrarySelectionStore();

  // Create memoized handler for clearing selection on empty space click - moved before conditionals
  const handleClearSelection = useCallback((e: React.MouseEvent) => {
    const { target } = e
    if (target instanceof Element) {
      if (
        target.classList.contains('overflow-y-auto') ||
        target.classList.contains('py-2') ||
        target.classList.contains('py-8')
      ) {
        clearSelection()
      }
    }
  }, [clearSelection])

  // Memoized empty array for parent query IDs
  const emptyParentAllQueryIds = useMemo(() => [], []);

  // Memoized empty array for queries
  const emptyQueriesArray = useMemo(() => [], []);

  // Memoized refresh handler
  const handleRefreshCollections = useCallback(() => {
    void refreshCollections();
  }, [refreshCollections]);

  // Memoized try again handler
  const handleTryAgain = useCallback(() => {
    void refreshCollections();
  }, [refreshCollections]);

  useEffect(() => {
    // Fetch root collections (no parent)
    void fetchCollections(undefined, true);
  }, [fetchCollections]);

  if (isLoading && collections.length === 0) {
    return (
      <div className={`${className} flex items-center justify-center py-8`}>
        <div className="text-gray-500 dark:text-gray-400">Loading collections...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${className} flex items-center justify-center py-8`}>
        <div className="text-red-500 dark:text-red-400 text-center">
          <ExclamationTriangleIcon className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm font-medium mb-1">Failed to load collections</p>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">{error}</p>
          <button type="button"
            onClick={handleTryAgain}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={className}
      onClick={handleClearSelection}
    >
      {/* Tree */}
      <div className="flex-1 overflow-y-auto">
        {collections.length === 0 ? (
          <div className="py-8 text-center">
            <FolderIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">No collections yet</p>
            {onCreateCollection && (
              <button type="button"
                onClick={onCreateCollection}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
              >
                Create your first collection
              </button>
            )}
          </div>
        ) : (
          <div className="py-2">
            {/* Only show root collections (no parentCollectionId) */}
            {collections.filter(c => !c.parentCollectionId).map((collection) => (
              <TreeNode
                key={collection.id}
                collection={collection}
                queries={emptyQueriesArray}
                level={0}
                onQuerySelect={onQuerySelect}
                onCreateQuery={onCreateQuery}
                onCreateFolder={onCreateFolder}
                selectedQueryId={selectedQueryId}
                onRefreshNeeded={handleRefreshCollections}
                parentAllQueryIds={emptyParentAllQueryIds} // Pass empty array instead of undefined to force collection of all query IDs
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};